import { useRef } from 'react';
import cx from 'classnames';
import './ChoiceCards.css';

/**
 * QHS ChoiceCards
 * ---------------
 * A radio group rendered as equal-weight cards: an icon tile, a title,
 * one line about what the choice does, and the radio dot RadioCard
 * draws. Use it where the choice is THE decision of a step rather than
 * a setting among others - e.g. how an assessment reaches the patient.
 * Nothing is selected until the user chooses, so the content that
 * belongs to the choice appears only once there is one; wrap that
 * content in `ChoiceAttachedPanel` so it visibly hangs off the chosen
 * card.
 *
 *   <ChoiceCards
 *     value={mode}
 *     onChange={setMode}
 *     ariaLabel="How will the patient take it"
 *     options={[
 *       { value: 'invite', title: 'Send invitation', description: '...', icon: <SendIcon /> },
 *       { value: 'clinic', title: 'Take in-clinic', description: '...', icon: <TabletIcon /> },
 *     ]}
 *   />
 *
 * Keyboard: the group is one tab stop; arrows move AND select, Space /
 * Enter select the focused card (the native radio idiom).
 */

/** The option focus lands on after an arrow key. */
export const nextChoiceIndex = (key, index, count) => {
  if (count <= 0) return -1;
  if (key === 'ArrowRight' || key === 'ArrowDown') return (index + 1) % count;
  if (key === 'ArrowLeft' || key === 'ArrowUp') return (index - 1 + count) % count;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  return -1;
};

const ChoiceCards = ({
  options = [],
  value = null,
  onChange,
  disabled = false,
  ariaLabel,
  ariaLabelledBy,
  columns,
  className,
}) => {
  const buttons = useRef([]);
  const selectedIndex = options.findIndex(option => option.value === value);
  // One tab stop: the chosen card, or the first one while nothing is chosen.
  const tabIndexOf = index => (index === (selectedIndex === -1 ? 0 : selectedIndex) ? 0 : -1);

  const choose = option => {
    if (disabled || option.disabled || option.value === value) return;
    onChange?.(option.value);
  };

  const onKeyDown = (event, index) => {
    const next = nextChoiceIndex(event.key, index, options.length);
    if (next !== -1) {
      event.preventDefault();
      buttons.current[next]?.focus();
      choose(options[next]);
      return;
    }
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      choose(options[index]);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={cx('qhs-choice', className)}
      style={columns ? { '--qhs-choice-cols': columns } : undefined}
    >
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <button
            key={String(option.value)}
            ref={node => {
              buttons.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={tabIndexOf(index)}
            disabled={disabled || option.disabled}
            className="qhs-choice__card"
            onClick={() => choose(option)}
            onKeyDown={event => onKeyDown(event, index)}
          >
            {option.icon && (
              <span className="qhs-choice__tile" aria-hidden="true">
                {option.icon}
              </span>
            )}
            <span className="qhs-choice__text">
              <span className="qhs-choice__title">{option.title}</span>
              {option.description && <span className="qhs-choice__desc">{option.description}</span>}
            </span>
            <span className="qhs-choice__dot" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
};

/**
 * The content that belongs to the chosen card. `index` / `count` put
 * the notch under that card; `active` false (nothing chosen yet) hides
 * the rule and the notch, so put the "choose to continue" hint inside.
 */
const ChoiceAttachedPanel = ({ active, index = 0, count = 2, className, children }) => {
  const notch = count > 0 ? ((Math.max(index, 0) + 0.5) / count) * 100 : 50;
  return (
    <div className={cx('qhs-choice-panel', active && 'qhs-choice-panel--on', className)}>
      {/* A real element, positioned inline: its `left` transition is
          what makes the notch slide to the other card (Safari did not
          animate a ::before whose position came from a custom property). */}
      <span className="qhs-choice-panel__notch" aria-hidden="true" style={{ left: `${notch}%` }} />
      {children}
    </div>
  );
};

const ChoiceHint = ({ children }) => <div className="qhs-choice-panel__hint">{children}</div>;

export { ChoiceCards, ChoiceAttachedPanel, ChoiceHint };
export default ChoiceCards;
